import { VdpTemplate } from '../types';

export interface CsvTemplatePreset {
  id: string;
  name: string;
  filename: string;
  description: string;
  headers: string[];
  rows: Record<string, string>[];
}

/**
 * Generates a tailored CSV data structure matching the active VDP template's dynamic bindings.
 */
export function generateTemplateAlignedCsv(template?: VdpTemplate | null): CsvTemplatePreset {
  const dynamicKeys = new Set<string>();

  if (template?.elements) {
    template.elements.forEach(el => {
      if (el.columnName) {
        dynamicKeys.add(el.columnName.replace(/[{}]/g, '').trim());
      }
      if (el.text && typeof el.text === 'string') {
        const matches = el.text.match(/\{([^}]+)\}/g);
        if (matches) {
          matches.forEach(m => dynamicKeys.add(m.replace(/[{}]/g, '').trim()));
        }
      }
    });
  }

  const templateFields = Array.from(dynamicKeys).filter(k => k.length > 0);

  if (templateFields.length > 0) {
    // Fill sample values tailored to the template's detected fields
    const headers = templateFields;
    const sampleRows: Record<string, string>[] = [
      generateSampleRowForFields(headers, 1),
      generateSampleRowForFields(headers, 2),
      generateSampleRowForFields(headers, 3),
      generateSampleRowForFields(headers, 4),
      generateSampleRowForFields(headers, 5),
    ];

    return {
      id: 'active_template',
      name: 'Active Template Bound Schema',
      filename: 'custom-template-data.csv',
      description: `Tailored CSV with ${headers.length} columns matched directly to your current design elements (${headers.join(', ')}).`,
      headers,
      rows: sampleRows
    };
  }

  // Fallback to Standard Retail VDP template
  return {
    id: 'standard_retail',
    name: 'Standard Retail Product & Barcode Template',
    filename: 'standard-vdp-product-template.csv',
    description: 'General-purpose retail template with SKUs, product names, prices, EAN-13 barcodes, and QR links.',
    headers: ['SKU', 'ProductName', 'Price', 'Barcode', 'BatchNo', 'Category', 'QrLink'],
    rows: [
      { SKU: 'PROD-101', ProductName: 'Artisan Dark Roast Coffee 250g', Price: '$14.99', Barcode: '8901234567891', BatchNo: 'LOT-2026-A1', Category: 'Beverages', QrLink: 'https://store.example.com/p/101' },
      { SKU: 'PROD-102', ProductName: 'Premium Wireless Studio Headphones', Price: '$129.50', Barcode: '8901234567907', BatchNo: 'LOT-2026-A2', Category: 'Electronics', QrLink: 'https://store.example.com/p/102' },
      { SKU: 'PROD-103', ProductName: 'Cold-Pressed Extra Virgin Olive Oil 500ml', Price: '$22.00', Barcode: '8901234567914', BatchNo: 'LOT-2026-B1', Category: 'Pantry', QrLink: 'https://store.example.com/p/103' },
      { SKU: 'PROD-104', ProductName: 'Handmade Lavender Botanical Soap Bar', Price: '$7.50', Barcode: '8901234567921', BatchNo: 'LOT-2026-B2', Category: 'Bath & Body', QrLink: 'https://store.example.com/p/104' },
      { SKU: 'PROD-105', ProductName: 'Braided Fast-Charging USB-C Cable 2m', Price: '$12.99', Barcode: '8901234567938', BatchNo: 'LOT-2026-C1', Category: 'Accessories', QrLink: 'https://store.example.com/p/105' },
    ]
  };
}

function generateSampleRowForFields(fields: string[], index: number): Record<string, string> {
  const row: Record<string, string> = {};
  fields.forEach(field => {
    const lower = field.toLowerCase();
    if (lower.includes('sku') || lower.includes('code') || lower.includes('id')) {
      row[field] = `ITEM-${1000 + index}`;
    } else if (lower.includes('barcode') || lower.includes('ean') || lower.includes('upc')) {
      const base = `89012345679${index}`;
      row[field] = base;
    } else if (lower.includes('price') || lower.includes('cost') || lower.includes('amount')) {
      row[field] = (19.99 + (index * 5)).toFixed(2);
    } else if (lower.includes('name') || lower.includes('title')) {
      const names = ['Sample Product Alpha', 'Sample Product Beta', 'Sample Product Gamma', 'Sample Product Delta', 'Sample Product Epsilon'];
      row[field] = names[(index - 1) % names.length];
    } else if (lower.includes('qr') || lower.includes('url') || lower.includes('link')) {
      row[field] = `https://example.com/item/${1000 + index}`;
    } else if (lower.includes('serial') || lower.includes('sn')) {
      row[field] = `SN-2026-${String(index).padStart(4, '0')}`;
    } else if (lower.includes('batch') || lower.includes('lot')) {
      row[field] = `LOT-2026-0${index}`;
    } else if (lower.includes('date') || lower.includes('exp')) {
      row[field] = '2026-12-31';
    } else if (lower.includes('company') || lower.includes('org')) {
      row[field] = `Acme Enterprise ${index}`;
    } else if (lower.includes('role') || lower.includes('tier')) {
      row[field] = index % 2 === 0 ? 'VIP Access' : 'Standard Delegate';
    } else {
      row[field] = `Sample Value ${index}`;
    }
  });
  return row;
}

export const TEMPLATE_PRESETS: CsvTemplatePreset[] = [
  {
    id: 'retail_barcode',
    name: 'Retail Barcodes & Product Pricing',
    filename: 'retail-products-template.csv',
    description: 'Includes SKU, Product Name, Retail Price, EAN-13 Barcode, Category, and QR URLs.',
    headers: ['SKU', 'ProductName', 'Price', 'Barcode', 'BatchNo', 'Category', 'QrLink'],
    rows: [
      { SKU: 'PROD-101', ProductName: 'Artisan Dark Roast Coffee 250g', Price: '$14.99', Barcode: '8901234567891', BatchNo: 'LOT-2026-A1', Category: 'Beverages', QrLink: 'https://store.example.com/p/101' },
      { SKU: 'PROD-102', ProductName: 'Premium Wireless Studio Headphones', Price: '$129.50', Barcode: '8901234567907', BatchNo: 'LOT-2026-A2', Category: 'Electronics', QrLink: 'https://store.example.com/p/102' },
      { SKU: 'PROD-103', ProductName: 'Cold-Pressed Extra Virgin Olive Oil 500ml', Price: '$22.00', Barcode: '8901234567914', BatchNo: 'LOT-2026-B1', Category: 'Pantry', QrLink: 'https://store.example.com/p/103' },
    ]
  },
  {
    id: 'event_badge',
    name: 'Event Badges & Conference Passes',
    filename: 'event-badges-template.csv',
    description: 'Includes Attendee Name, Organization, Role, Access Tier, Badge ID, and QR Pass URLs.',
    headers: ['AttendeeName', 'Company', 'Role', 'AccessTier', 'BadgeID', 'QrCode'],
    rows: [
      { AttendeeName: 'Alex Rivera', Company: 'Apex Global Technologies', Role: 'Keynote Speaker', AccessTier: 'VIP All-Access', BadgeID: 'SPK-101', QrCode: 'https://event.io/pass/SPK-101' },
      { AttendeeName: 'Elena Rostova', Company: 'BioHealth Labs', Role: 'Senior Researcher', AccessTier: 'Exhibitor', BadgeID: 'EXH-501', QrCode: 'https://event.io/pass/EXH-501' },
      { AttendeeName: 'Marcus Vance', Company: 'Vance Logistics Co.', Role: 'Delegate', AccessTier: 'Standard', BadgeID: 'DEL-890', QrCode: 'https://event.io/pass/DEL-890' },
    ]
  },
  {
    id: 'asset_inventory',
    name: 'Industrial Asset Tags & Serial Numbers',
    filename: 'asset-inventory-template.csv',
    description: 'Includes Asset Tag, Equipment Type, Location, Serial Number, Inspection Date, and Tracking URL.',
    headers: ['AssetTag', 'EquipmentType', 'Location', 'SerialNumber', 'InspectionDate', 'TrackURL'],
    rows: [
      { AssetTag: 'AST-8001', EquipmentType: 'Robotic Arm Conveyor', Location: 'Zone A - Line 4', SerialNumber: 'SN-99201-RA', InspectionDate: '2026-09-15', TrackURL: 'https://mfg.internal/asset/8001' },
      { AssetTag: 'AST-8002', EquipmentType: 'Hydraulic Press 50T', Location: 'Zone A - Line 2', SerialNumber: 'SN-99202-HP', InspectionDate: '2026-07-22', TrackURL: 'https://mfg.internal/asset/8002' },
      { AssetTag: 'AST-8003', EquipmentType: 'Laser Cutting Unit', Location: 'Zone B - Bay 3', SerialNumber: 'SN-99203-LC', InspectionDate: '2026-08-10', TrackURL: 'https://mfg.internal/asset/8003' },
    ]
  },
  {
    id: 'shipping_logistics',
    name: 'Logistics & Shipping Pallet Labels',
    filename: 'shipping-labels-template.csv',
    description: 'Includes Tracking Number, Recipient Name, Destination Address, Weight, Package Type, and Routing Barcode.',
    headers: ['TrackingNumber', 'Recipient', 'Address', 'CityStateZip', 'WeightKg', 'PackageType', 'RoutingBarcode'],
    rows: [
      { TrackingNumber: 'TRK-98234101', Recipient: 'Acme Distribution Hub', Address: '742 Evergreen Terrace', CityStateZip: 'Springfield, OR 97477', WeightKg: '14.2', PackageType: 'Pallet Box', RoutingBarcode: '98234101001' },
      { TrackingNumber: 'TRK-98234102', Recipient: 'Metro Fulfillment West', Address: '1200 Industrial Pkwy', CityStateZip: 'Seattle, WA 98101', WeightKg: '8.7', PackageType: 'Carton', RoutingBarcode: '98234102002' },
      { TrackingNumber: 'TRK-98234103', Recipient: 'Pacific Freight Express', Address: '450 Harbor Blvd', CityStateZip: 'Long Beach, CA 90802', WeightKg: '22.5', PackageType: 'Crate', RoutingBarcode: '98234103003' },
    ]
  }
];

/**
 * Serializes CSV headers and rows into properly escaped delimited string.
 */
export function formatCsvString(headers: string[], rows: Record<string, string>[], delimiter = ','): string {
  const escapeCell = (val: any): string => {
    if (val === null || val === undefined) return '';
    const str = String(val);
    if (str.includes(delimiter) || str.includes('"') || str.includes('\n') || str.includes('\r')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const headerLine = headers.map(escapeCell).join(delimiter);
  const rowLines = rows.map(row => {
    return headers.map(h => escapeCell(row[h] ?? '')).join(delimiter);
  });

  return [headerLine, ...rowLines].join('\r\n');
}

/**
 * Triggers a browser download of the CSV file.
 */
export function triggerCsvDownload(filename: string, csvContent: string): void {
  // Use UTF-8 BOM so Excel opens special characters correctly
  const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
